import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Typography, Space, Divider, message, Spin, Tag, Form } from 'antd';
import { EditOutlined, GoogleOutlined, CalendarOutlined, FileTextOutlined, StarOutlined, CheckOutlined, CloseOutlined, CreditCardOutlined } from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';
import { useBillingStore } from '../stores/billingStore';
import { useNavigate } from 'react-router-dom';
import { billingService } from '../services/billingService';
import apiClient from '../services/api';
import UserAvatar from './UserAvatar';

const { Title, Text } = Typography;

const MyProfile = ({ visible, onClose, onShowPurchaseCredits, notesStats }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // 移除 userStats 和 statsLoading，直接使用 notesStats
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [editingUsername, setEditingUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  const { user, setAuthState } = useAuthStore();
  const { credits } = useBillingStore();
  const navigate = useNavigate();

  // 移除 fetchUserStats 函数，直接使用 notesStats props

  // 获取订阅状态
  useEffect(() => {
    if (visible) {
      const fetchSubscription = async () => {
        try {
          const status = await billingService.getSubscriptionStatus();
          setSubscriptionStatus(status);
        } catch (error) {
          console.error('Failed to fetch subscription status:', error);
        }
      };
      fetchSubscription();
    }
  }, [visible]);

  // Determine plan type based on subscription status
  const getPlanType = () => {
    if (subscriptionStatus?.hasSubscription && subscriptionStatus?.status === 'active') {
      return 'Pro';
    }
    return 'Starter';
  };


  // 计算使用天数
  const calculateDaysUsed = () => {
    console.log('[calculateDaysUsed] start calculating days used');
    console.log('[calculateDaysUsed] user object:', user);
    console.log('[calculateDaysUsed] user?.created_at:', user?.created_at);
    
    if (!user?.created_at) {
      console.log('[calculateDaysUsed] user.created_at is empty or missing, return 0');
      return 0;
    }
    
    try {
      console.log('[calculateDaysUsed] raw user.created_at:', user.created_at);
      const createdDate = new Date(user.created_at);
      console.log('[calculateDaysUsed] parsed createdDate:', createdDate);
      
      if (isNaN(createdDate.getTime())) {
        console.error('[calculateDaysUsed] createdDate is invalid, return 0');
        return 0;
      }
      
      const now = new Date();
      console.log('[calculateDaysUsed] current time now:', now);
      
      const diffTime = Math.abs(now.getTime() - createdDate.getTime());
      console.log('[calculateDaysUsed] diffTime (ms):', diffTime);
      
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      console.log('[calculateDaysUsed] result diffDays:', diffDays);
      
      return diffDays;
    } catch (e) {
      console.error('[calculateDaysUsed] failed to calculate days used:', e);
      return 0;
    }
  };

  // 获取笔记数量 - 使用 notesStats 数据
  const getNotesCount = () => {
    return notesStats?.totalNotes || 0;
  };

  // 获取收藏数量 - 使用 notesStats 数据
  const getCollectionsCount = () => {
    return notesStats?.pinnedNotes || 0;
  };

  // 初始化数据
  useEffect(() => {
    if (visible && user) {
      console.log('[MyProfile] init, user data:', user);
      console.log('[MyProfile] user.created_at:', user.created_at);
      
      // 只有在非编辑状态下才设置编辑用户名
      if (!isEditingUsername) {
        setEditingUsername(user.username);
      }
      
      // Proactively fetch latest user info
      fetchLatestUserInfo();
    }
  }, [visible, user?.id]); // 只依赖 user.id 而不是整个 user 对象

  // 获取最新的用户信息
  const fetchLatestUserInfo = async () => {
    try {
      console.log('[MyProfile] fetching latest user info');
      const response = await apiClient.get('/users/profile');
      console.log('[MyProfile] profile response:', response.data);
      
      if (response.data?.data?.user) {
        const latestUserInfo = response.data.data.user;
        console.log('[MyProfile] latest user info:', latestUserInfo);
        console.log('[MyProfile] latest created_at:', latestUserInfo.created_at);
        
        // Only update when not editing to avoid resetting input
        if (!isEditingUsername) {
          setAuthState({
            user: latestUserInfo
          });
        } else {
          console.log('[MyProfile] user is editing username, skip updating user state');
        }
      }
    } catch (error) {
      console.error('[MyProfile] failed to fetch latest user info:', error);
    }
  };

  // 验证用户名
  const validateUsername = (username) => {
    // 检查是否为空或null或undefined
    if (!username || username === null || username === undefined) {
      return 'Username cannot be empty';
    }
    
    // 检查是否为空字符串或只包含空格
    if (username.trim() === '' || username.trim().length === 0) {
      return 'Username cannot be empty';
    }
    
    // 检查长度（基于trim后的长度）
    const trimmedUsername = username.trim();
    if (trimmedUsername.length < 2 || trimmedUsername.length > 20) {
      return 'Username length must be between 2 and 20 characters';
    }
    
    // 检查字符格式
    if (!/^[a-zA-Z0-9_\u4e00-\u9fa5\s]+$/.test(username)) {
      return 'Username can only include letters, numbers, underscores, spaces, and Chinese characters';
    }
    
    return '';
  };

  // 开始编辑用户名
  const handleStartEditUsername = () => {
    console.log('[handleStartEditUsername] start editing username');
    console.log('[handleStartEditUsername] current user.username:', user.username);
    setIsEditingUsername(true);
    setEditingUsername(user.username);
    setUsernameError('');
  };

  // 取消编辑用户名
  const handleCancelEditUsername = () => {
    setIsEditingUsername(false);
    setEditingUsername(user.username);
    setUsernameError('');
  };

  // 保存用户名
  const handleSaveUsername = async () => {
    // Strict empty check
    if (!editingUsername || editingUsername.trim() === '') {
      setUsernameError('Username cannot be empty');
      return;
    }

    // Validate username
    const error = validateUsername(editingUsername);
    if (error) {
      setUsernameError(error);
      return;
    }

    if (editingUsername.trim() === user.username) {
      setIsEditingUsername(false);
      setUsernameError('');
      return;
    }

    setSaving(true);
    setUsernameError('');
    
    try {
      const response = await apiClient.put('/users/profile', {
        username: editingUsername
      });

      // Update local user info
      setAuthState({
        user: {
          ...user,
          username: editingUsername
        }
      });
      
      message.success('Username updated successfully');
      setIsEditingUsername(false);
      setUsernameError('');
    } catch (error) {
      console.error('Failed to update username:', error);
      if (error.response?.data?.message) {
        setUsernameError(error.response.data.message);
      } else {
        setUsernameError('Failed to update username');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle username input change
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    console.log('[handleUsernameChange] input value:', value);
    console.log('[handleUsernameChange] current editingUsername:', editingUsername);
    console.log('[handleUsernameChange] current isEditingUsername:', isEditingUsername);
    
    setEditingUsername(value);
    
    // Realtime validation
    if (value !== undefined && value !== null) {
      if (value.trim() === '') {
        setUsernameError('Username cannot be empty');
      } else if (value !== user.username) {
        const error = validateUsername(value);
        setUsernameError(error);
      } else {
        setUsernameError('');
      }
    } else {
      setUsernameError('');
    }
  };

  // Handle renewal button click
  const handleRenewal = () => {
    onClose();
    onShowPurchaseCredits();
  };

  // 获取用户头像 - 使用与UserMenu相同的UserAvatar组件
  const getUserAvatar = () => {
    return (
      <UserAvatar 
        user={user}
        size={64}
        autoGenerate={true}
      />
    );
  };

  // 判断是否为Google注册用户
  const isGoogleUser = () => {
    return user?.auth_provider === 'google' || user?.google_id;
  };

  return (
    <Modal
      title="Profile"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      <div style={{ padding: '20px 0' }}>
        {/* 用户基本信息区域 - 左右布局 */}
        <div style={{ 
          marginBottom: 32,
          padding: '24px',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '20px'
        }}>
          {/* 左侧头像 */}
          <div style={{ flexShrink: 0 }}>
            {getUserAvatar()}
          </div>
          
          {/* 右侧用户信息 */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* 用户名编辑区域 */}
            <div style={{ marginBottom: 12 }}>
              {isEditingUsername ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Input
                      value={editingUsername}
                      onChange={handleUsernameChange}
                      onPressEnter={handleSaveUsername}
                      style={{ flex: 1 }}
                      maxLength={20}
                      disabled={saving}
                      status={usernameError ? 'error' : ''}
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<CheckOutlined />}
                      onClick={handleSaveUsername}
                      loading={saving}
                      disabled={saving || !!usernameError}
                    />
                    <Button
                      size="small"
                      icon={<CloseOutlined />}
                      onClick={handleCancelEditUsername}
                      disabled={saving}
                    />
                  </div>
                  {/* 错误信息显示 */}
                  {usernameError && (
                    <div style={{ 
                      color: '#ff4d4f', 
                      fontSize: '12px', 
                      marginTop: '4px',
                      marginLeft: '4px'
                    }}>
                      {usernameError}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Title level={3} style={{ margin: 0 }}>
                    {user?.username}
                  </Title>
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={handleStartEditUsername}
                    style={{ 
                      padding: '4px',
                      height: 'auto',
                      color: '#666'
                    }}
                  />
                </div>
              )}
            </div>
            
            {/* 邮箱地址 */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                {user?.email}
              </Text>
            </div>
            
            {/* Google注册标识 */}
            {isGoogleUser() && (
              <div style={{ marginBottom: 12 }}>
                <Tag icon={<GoogleOutlined />} color="blue">
                  Google account
                </Tag>
              </div>
            )}
            
            {/* Plan Type - clickable */}
            <div
              style={{
                fontSize: '14px',
                color: getPlanType() === 'Pro' ? '#1890ff' : '#8c8c8c',
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                marginBottom: '8px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
                navigate('/membership');
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1890ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = getPlanType() === 'Pro' ? '#1890ff' : '#8c8c8c';
              }}
            >
              <span style={{ fontWeight: '500' }}>{getPlanType()} Plan</span>
              {getPlanType() !== 'Pro' && (
                <span
                  style={{
                    fontSize: '12px',
                    marginLeft: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#ff4d4f',
                    fontWeight: '500',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: '#fff1f0',
                    border: '1px solid #ffccc7'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    navigate('/membership');
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#ff7875';
                    e.currentTarget.style.backgroundColor = '#ffe7e6';
                    e.currentTarget.style.borderColor = '#ffa39e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#ff4d4f';
                    e.currentTarget.style.backgroundColor = '#fff1f0';
                    e.currentTarget.style.borderColor = '#ffccc7';
                  }}
                >
                  <CreditCardOutlined style={{ fontSize: '12px' }} />
                  <span>Subs</span>
                </span>
              )}
            </div>

            {/* 积分信息 
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12
            }}>
              <Text strong style={{ fontSize: '16px' }}>
                Remaining credits: {credits || 0}
              </Text>
              <Button 
                type="primary" 
                size="small"
                onClick={handleRenewal}
                style={{ 
                  backgroundColor: '#52c41a',
                  borderColor: '#52c41a'
                }}
              >
                Renew
              </Button>
            </div> */}
          </div>
        </div>

        {/* 用户统计信息 */}
        <div style={{ marginBottom: 32 }}>
          <Title level={4} style={{ marginBottom: 16 }}>
            Usage statistics
          </Title>
          
          {notesStats ? (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '16px'
            }}>
              {/* 使用天数 */}
              <div style={{
                textAlign: 'center',
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)'
              }}>
                <CalendarOutlined style={{ fontSize: '24px', color: '#1890ff', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#262626' }}>
                  {calculateDaysUsed()}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Days used
                </div>
              </div>

              {/* 笔记数量 */}
              <div style={{
                textAlign: 'center',
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)'
              }}>
                <FileTextOutlined style={{ fontSize: '24px', color: '#52c41a', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#262626' }}>
                  {getNotesCount()}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Notes
                </div>
              </div>

              {/* 收藏数量 */}
              <div style={{
                textAlign: 'center',
                padding: '16px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #e8e8e8',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)'
              }}>
                <StarOutlined style={{ fontSize: '24px', color: '#fa8c16', marginBottom: '8px' }} />
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#262626' }}>
                  {getCollectionsCount()}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Favorites
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
              Loading statistics...
            </div>
          )}
        </div>

        <Divider />

        {/* 邮箱信息提示 */}
        <div style={{ 
          backgroundColor: '#fff7e6', 
          padding: '12px', 
          borderRadius: '6px',
          marginBottom: 24,
          border: '1px solid #ffd591'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: '#fa8c16', fontSize: '16px' }}>ℹ️</span>
            <Text style={{ fontSize: '14px', color: '#d46b08' }}>
              Email address cannot be changed. Contact admin if you need to update it.
            </Text>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} size="large">
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default MyProfile;
